import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, X, Users, Building2, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable Party Selector Component with inline party creation
 * 
 * @param {Object} props
 * @param {Array} props.parties - List of all available parties
 * @param {Array} props.selectedIds - Currently selected party IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {Function} props.onPartyCreated - Callback when new party is created (to refresh parties list)
 * @param {string} props.partyTypeFilter - Filter parties by type ('bv', 'private', or null for all)
 * @param {string} props.label - Label for the section
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.colorScheme - Color scheme ('emerald', 'amber', 'blue')
 * @param {React.ReactNode} props.icon - Icon component
 */
export const PartySelector = ({
  parties = [],
  selectedIds = [],
  onSelectionChange,
  onPartyCreated,
  partyTypeFilter = null,
  label,
  placeholder = 'Selecteer partij',
  colorScheme = 'emerald',
  icon
}) => {
  const [showNewPartyDialog, setShowNewPartyDialog] = useState(false);
  const [newPartyForm, setNewPartyForm] = useState({
    name: '',
    party_type: partyTypeFilter || 'bv',
    contact_email: '',
    contact_phone: '',
    kvk_number: ''
  });
  const [saving, setSaving] = useState(false);

  // Filter parties based on type and already selected
  const filteredParties = parties.filter(p => {
    if (partyTypeFilter && p.party_type !== partyTypeFilter) return false;
    if (selectedIds.includes(p.party_id)) return false;
    return true;
  });

  const handleAddParty = (partyId) => {
    if (partyId === '__new__') {
      setShowNewPartyDialog(true);
      return;
    }
    if (partyId && !selectedIds.includes(partyId)) {
      onSelectionChange([...selectedIds, partyId]);
    }
  };

  const handleRemoveParty = (partyId) => {
    onSelectionChange(selectedIds.filter(id => id !== partyId));
  };

  const handleCreateParty = async (e) => {
    e.preventDefault();
    if (!newPartyForm.name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/parties`, newPartyForm, { withCredentials: true });
      const newParty = response.data;
      
      toast.success(`Partij "${newParty.name}" aangemaakt`);
      
      // Add the new party to selection
      onSelectionChange([...selectedIds, newParty.party_id]);
      
      // Notify parent to refresh parties list
      if (onPartyCreated) {
        onPartyCreated(newParty);
      }
      
      // Reset form and close dialog
      setNewPartyForm({
        name: '',
        party_type: partyTypeFilter || 'bv',
        contact_email: '',
        contact_phone: '',
        kvk_number: ''
      });
      setShowNewPartyDialog(false);
    } catch (error) {
      console.error('Error creating party:', error);
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken partij');
    } finally {
      setSaving(false);
    }
  };

  // Color schemes
  const colors = {
    emerald: {
      bg: 'bg-emerald-900/50',
      text: 'text-emerald-300',
      border: 'border-emerald-700'
    },
    amber: {
      bg: 'bg-amber-900/50',
      text: 'text-amber-300',
      border: 'border-amber-700'
    },
    blue: {
      bg: 'bg-blue-900/50',
      text: 'text-blue-300',
      border: 'border-blue-700'
    }
  };

  const colorStyle = colors[colorScheme] || colors.emerald;

  return (
    <>
      <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
        <Label className="text-slate-300 flex items-center gap-2">
          {icon}
          {label}
        </Label>
        <div className="flex gap-2">
          <Select onValueChange={handleAddParty}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
              {/* Option to add new party */}
              <SelectItem value="__new__" className="text-[#b4976f] hover:bg-slate-800 font-medium">
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Nieuwe partij toevoegen...
                </span>
              </SelectItem>
              {/* Divider */}
              {filteredParties.length > 0 && (
                <div className="border-t border-slate-700 my-1" />
              )}
              {/* Existing parties */}
              {filteredParties.map((party) => (
                <SelectItem key={party.party_id} value={party.party_id} className="text-white hover:bg-slate-800">
                  <span className="flex items-center gap-2">
                    {party.party_type === 'bv' ? <Building2 className="h-4 w-4 text-slate-400" /> : <User className="h-4 w-4 text-slate-400" />}
                    {party.name} {party.party_type === 'bv' ? '(B.V.)' : '(Particulier)'}
                  </span>
                </SelectItem>
              ))}
              {filteredParties.length === 0 && (
                <div className="text-slate-500 text-sm p-2 text-center">
                  Geen partijen beschikbaar
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* Selected parties */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedIds.map((id) => {
              const party = parties.find(p => p.party_id === id);
              return (
                <span key={id} className={`inline-flex items-center gap-1 px-2 py-1 rounded ${colorStyle.bg} text-sm ${colorStyle.text}`}>
                  {party?.party_type === 'bv' ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                  {party?.name || id}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveParty(id)} 
                    className="text-red-400 hover:text-red-300 ml-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* New Party Dialog */}
      <Dialog open={showNewPartyDialog} onOpenChange={setShowNewPartyDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#b4976f]">Nieuwe Partij Toevoegen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateParty} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Naam *</Label>
              <Input
                value={newPartyForm.name}
                onChange={(e) => setNewPartyForm({ ...newPartyForm, name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Naam van de partij"
                required
                data-testid="new-party-name"
              />
            </div>
            
            {!partyTypeFilter && (
              <div className="space-y-2">
                <Label className="text-slate-300">Type</Label>
                <Select 
                  value={newPartyForm.party_type} 
                  onValueChange={(value) => setNewPartyForm({ ...newPartyForm, party_type: value })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="bv" className="text-white hover:bg-slate-700">B.V. (Rechtspersoon)</SelectItem>
                    <SelectItem value="private" className="text-white hover:bg-slate-700">Particulier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(newPartyForm.party_type === 'bv' || partyTypeFilter === 'bv') && (
              <div className="space-y-2">
                <Label className="text-slate-300">KVK Nummer</Label>
                <Input
                  value={newPartyForm.kvk_number}
                  onChange={(e) => setNewPartyForm({ ...newPartyForm, kvk_number: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="12345678"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">E-mail</Label>
              <Input
                type="email"
                value={newPartyForm.contact_email}
                onChange={(e) => setNewPartyForm({ ...newPartyForm, contact_email: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="email@voorbeeld.nl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Telefoon</Label>
              <Input
                value={newPartyForm.contact_phone}
                onChange={(e) => setNewPartyForm({ ...newPartyForm, contact_phone: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="+31 6 12345678"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewPartyDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                Annuleren
              </Button>
              <Button
                type="submit"
                className="bg-[#b4976f] hover:bg-[#a38663]"
                disabled={saving}
              >
                {saving ? 'Opslaan...' : 'Partij Aanmaken'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartySelector;
